import { apiClient } from '@/lib/api/client'
import axios from 'axios'

export interface ReceivedEmail {
  id: number
  message_id: string
  subject: string | null
  from_name: string | null
  from_email: string | null
  body_preview: string | null
  /** Present only on GET /support/inbox/:id (fetched live from Microsoft Graph). */
  body?: string
  received_at: string | null
  is_read: boolean
  created_at: string
  updated_at: string
}

export interface InboxListMeta {
  total: number
  per_page: number
  current_page: number
  last_page: number
  unread_count: number
}

export interface InboxListResponse {
  data: ReceivedEmail[]
  meta: InboxListMeta
}

export interface EmailThread {
  thread_subject: string
  email_count: number
  unread_count: number
  latest_id: number | null
  latest_from: string | null
  latest_at: string | null
  preview: string | null
  emails: ReceivedEmail[]
}

const INBOX_REQUEST_TIMEOUT_MS = 45000
const INBOX_MAX_RETRIES = 2

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function getInboxEmails(params?: {
  page?: number
  per_page?: number
  search?: string
  is_read?: '0' | '1'
}): Promise<InboxListResponse> {
  let attempt = 0

  while (attempt <= INBOX_MAX_RETRIES) {
    try {
      const { data } = await apiClient.get<InboxListResponse>('/support/inbox', {
        params,
        timeout: INBOX_REQUEST_TIMEOUT_MS,
      })
      return data
    } catch (error) {
      const isAxiosError = axios.isAxiosError(error)
      const errorCode = isAxiosError ? error.code : undefined
      const isTimeout = errorCode === 'ECONNABORTED'
      const isNetworkError = isAxiosError && !error.response && errorCode !== 'ERR_CANCELED'
      const canRetry = (isTimeout || isNetworkError) && attempt < INBOX_MAX_RETRIES

      if (!canRetry) {
        throw error
      }

      // Small exponential backoff to reduce burst retries when API is under load.
      const backoffMs = 400 * (attempt + 1)
      await wait(backoffMs)
      attempt += 1
    }
  }

  throw new Error('Inbox request failed after retries')
}

export async function getEmailDetail(id: number): Promise<ReceivedEmail> {
  const { data } = await apiClient.get<{ data: ReceivedEmail }>(`/support/inbox/${id}`)
  return data.data
}

export async function markEmailAsRead(id: number): Promise<ReceivedEmail> {
  const { data } = await apiClient.put<{ data: ReceivedEmail }>(`/support/inbox/${id}/mark-read`)
  return data.data
}

export async function getEmailThreads(params?: {
  search?: string
  is_read?: '0' | '1'
}): Promise<{ data: EmailThread[] }> {
  const { data } = await apiClient.get<{ data: EmailThread[] }>('/support/inbox/threads', { params })
  return data
}

export async function replyToEmail(id: number, body: string): Promise<void> {
  await apiClient.post(`/support/inbox/${id}/reply`, { body })
}

export async function forwardEmail(id: number, to: string, body: string): Promise<void> {
  await apiClient.post(`/support/inbox/${id}/forward`, { to, body })
}
